import { API_BASE } from '../config/api';
import AuthService from './authService';
import generateId from '../utils/generateId';

// Short-TTL + in-flight dedup cache for the full-preferences GET (same
// pattern as AuthService.getCurrentUser). Home mounts two hooks
// (useHomeSearch + useCurrentLocation) that both resolve to this endpoint
// concurrently, and both re-fire on every settingsUpdated event — without
// this, every Home visit issued two identical requests. Keyed by the auth
// header so a user switch can never serve another user's preferences.
// Mutations invalidate.
let _prefsCache = { key: null, data: null, timestamp: 0, pending: null };
const PREFS_CACHE_TTL = 30000; // 30 seconds

const invalidatePrefsCache = () => {
  _prefsCache = { key: null, data: null, timestamp: 0, pending: null };
};

// Every object getUserPreferencesAsync answers from the device fallback
// (signed out, non-2xx, network error) is remembered here, so a caller that
// is about to write the whole blob back can tell "the server said this" from
// "the server could not be read" (BH-10-02). Reads keep the fallback; a
// read-modify-write must not save it over the account.
const _fallbackResults = new WeakSet();
const markFallback = (prefs) => {
  if (prefs && typeof prefs === 'object') _fallbackResults.add(prefs);
  return prefs;
};

class UserPreferencesService {
  static API_PREFS = `${API_BASE}/api/preferences`;

  static async getCurrentUser() {
    const user = await AuthService.getCurrentUser();
    return user || { id: 'guest', name: 'Guest' };
  }

  // === DATABASE-FIRST PREFERENCE METHODS ===

  // Get all user preferences (database-first with localStorage fallback).
  // Successful responses are cached briefly and concurrent calls share one
  // request; fallback results are never cached.
  static async getUserPreferencesAsync(userId = null) {
    try {
      if (!AuthService.isAuthenticated()) {
        console.warn('⚠️ No auth token, falling back to localStorage');
        invalidatePrefsCache();
        return markFallback(this.getPreferencesFromLocalStorage());
      }

      const headers = AuthService.getJsonHeaders();
      const cacheKey = headers.Authorization || null;

      if (_prefsCache.key === cacheKey) {
        if (_prefsCache.data && (Date.now() - _prefsCache.timestamp) < PREFS_CACHE_TTL) {
          return _prefsCache.data;
        }
        if (_prefsCache.pending) {
          return _prefsCache.pending;
        }
      }

      const request = fetch(`${UserPreferencesService.API_PREFS}`, { headers })
        .then(async (response) => {
          if (response.ok) {
            const result = await response.json();
            const prefs = result.preferences || {};
            _prefsCache = { key: cacheKey, data: prefs, timestamp: Date.now(), pending: null };
            return prefs;
          }
          console.warn('⚠️ Database preferences failed, falling back to localStorage');
          invalidatePrefsCache();
          return markFallback(this.getPreferencesFromLocalStorage());
        })
        .catch((error) => {
          console.error('Error loading preferences from database:', error);
          console.log('📁 Falling back to localStorage preferences');
          invalidatePrefsCache();
          return markFallback(this.getPreferencesFromLocalStorage());
        });

      _prefsCache = { key: cacheKey, data: null, timestamp: 0, pending: request };
      return request;
    } catch (error) {
      console.error('Error loading preferences from database:', error);
      console.log('📁 Falling back to localStorage preferences');
      return markFallback(this.getPreferencesFromLocalStorage());
    }
  }

  // True when `prefs` (a getUserPreferencesAsync result) came from the server,
  // false when it is the device fallback because the server could not be read.
  static isServerResult(prefs) {
    return !!prefs && typeof prefs === 'object' && !_fallbackResults.has(prefs);
  }

  // Get preferences by category (database-first)
  static async getPreferencesByCategoryAsync(category, userId = null) {
    try {
      if (!AuthService.isAuthenticated()) {
        console.warn('⚠️ No auth token, falling back to localStorage');
        return this.getCategoryFromLocalStorage(category);
      }

      const response = await fetch(`${UserPreferencesService.API_PREFS}/${category}`, {
        headers: AuthService.getJsonHeaders()
      });

      if (response.ok) {
        const result = await response.json();
        return result.preferences || {};
      } else {
        console.warn(`⚠️ Database ${category} preferences failed, falling back to localStorage`);
        return this.getCategoryFromLocalStorage(category);
      }
    } catch (error) {
      console.error(`Error loading ${category} preferences from database:`, error);
      return this.getCategoryFromLocalStorage(category);
    }
  }

  // Set single preference (database-first)
  static async setPreferenceAsync(category, key, value, userId = null) {
    try {
      if (!AuthService.isAuthenticated()) {
        console.warn('⚠️ No auth token, falling back to localStorage');
        return this.setPreferenceInLocalStorage(category, key, value);
      }

      const response = await fetch(`${UserPreferencesService.API_PREFS}/${category}/${key}`, {
        method: 'PUT',
        headers: AuthService.getJsonHeaders(),
        body: JSON.stringify({ value })
      });

      if (response.ok) {
        const result = await response.json();
        invalidatePrefsCache();
        console.log(`✅ Set ${category}.${key} preference in database`);
        return result;
      } else {
        console.warn('⚠️ Database preference set failed, falling back to localStorage');
        return this.setPreferenceInLocalStorage(category, key, value);
      }
    } catch (error) {
      console.error('Error setting preference in database:', error);
      console.log('📁 Falling back to localStorage preference set');
      return this.setPreferenceInLocalStorage(category, key, value);
    }
  }

  // Set multiple preferences (database-first)
  static async setMultiplePreferencesAsync(preferences, userId = null) {
    try {
      if (!AuthService.isAuthenticated()) {
        console.warn('⚠️ No auth token, falling back to localStorage');
        return this.setMultiplePreferencesInLocalStorage(preferences);
      }

      const response = await fetch(`${UserPreferencesService.API_PREFS}`, {
        method: 'POST',
        headers: AuthService.getJsonHeaders(),
        body: JSON.stringify({ preferences })
      });

      if (response.ok) {
        const result = await response.json();
        invalidatePrefsCache();
        console.log(`✅ Set multiple preferences in database: ${result.updatedCount} updated`);
        return result;
      } else {
        console.warn('⚠️ Database bulk preference set failed, falling back to localStorage');
        return this.setMultiplePreferencesInLocalStorage(preferences);
      }
    } catch (error) {
      console.error('Error setting multiple preferences in database:', error);
      console.log('📁 Falling back to localStorage bulk preference set');
      return this.setMultiplePreferencesInLocalStorage(preferences);
    }
  }

  // Delete preference (database-first)
  static async deletePreferenceAsync(category, key, userId = null) {
    try {
      if (!AuthService.isAuthenticated()) {
        console.warn('⚠️ No auth token, falling back to localStorage');
        return this.deletePreferenceFromLocalStorage(category, key);
      }

      const response = await fetch(`${UserPreferencesService.API_PREFS}/${category}/${key}`, {
        method: 'DELETE',
        headers: AuthService.getJsonHeaders()
      });

      if (response.ok) {
        const result = await response.json();
        invalidatePrefsCache();
        console.log(`✅ Deleted ${category}.${key} preference from database`);
        return result;
      } else {
        console.warn('⚠️ Database preference delete failed, falling back to localStorage');
        return this.deletePreferenceFromLocalStorage(category, key);
      }
    } catch (error) {
      console.error('Error deleting preference from database:', error);
      console.log('📁 Falling back to localStorage preference delete');
      return this.deletePreferenceFromLocalStorage(category, key);
    }
  }

  // === DEVICE PREFERENCES ===

  // Get device preferences (database-first)
  static async getDevicePreferencesAsync(deviceId = null, userId = null) {
    try {
      const currentUser = userId || this.getCurrentUser().id;
      const currentDeviceId = deviceId || this.getDeviceId();
      
      const response = await fetch(`${UserPreferencesService.API_PREFS}/device/user/${currentUser}/${currentDeviceId}`);
      if (response.ok) {
        const result = await response.json();
        console.log('📦 Loaded device preferences from database');
        return result.preferences || {};
      } else {
        console.warn('⚠️ Database device preferences failed, falling back to localStorage');
        return this.getDevicePreferencesFromLocalStorage();
      }
    } catch (error) {
      console.error('Error loading device preferences from database:', error);
      return this.getDevicePreferencesFromLocalStorage();
    }
  }

  // Set device preferences (database-first)
  static async setDevicePreferencesAsync(preferences, deviceId = null, userId = null) {
    try {
      const currentUser = userId || this.getCurrentUser().id;
      const currentDeviceId = deviceId || this.getDeviceId();
      const deviceType = this.getDeviceType();
      
      const response = await fetch(`${UserPreferencesService.API_PREFS}/device/user/${currentUser}/${currentDeviceId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          deviceType,
          ...preferences
        })
      });

      if (response.ok) {
        const result = await response.json();
        console.log('✅ Set device preferences in database');
        return result;
      } else {
        console.warn('⚠️ Database device preferences set failed, falling back to localStorage');
        return this.setDevicePreferencesInLocalStorage(preferences);
      }
    } catch (error) {
      console.error('Error setting device preferences in database:', error);
      return this.setDevicePreferencesInLocalStorage(preferences);
    }
  }

  // === ACTIVITY PREFERENCES ===

  // Get activity preferences (database-first)
  static async getActivityPreferencesAsync(category, userId = null) {
    try {
      const currentUser = userId || this.getCurrentUser().id;
      
      const response = await fetch(`${UserPreferencesService.API_PREFS}/activity/user/${currentUser}/${category}`);
      if (response.ok) {
        const result = await response.json();
        console.log(`📦 Loaded ${category} activity preferences from database`);
        return result.preferences || [];
      } else {
        console.warn(`⚠️ Database ${category} activity preferences failed, falling back to localStorage`);
        return this.getActivityPreferencesFromLocalStorage(category);
      }
    } catch (error) {
      console.error(`Error loading ${category} activity preferences from database:`, error);
      return this.getActivityPreferencesFromLocalStorage(category);
    }
  }

  // Set activity preference (database-first)
  static async setActivityPreferenceAsync(category, subcategory, value, weight = 1.0, learnedFrom = 'explicit', userId = null) {
    try {
      const currentUser = userId || this.getCurrentUser().id;
      
      const response = await fetch(`${UserPreferencesService.API_PREFS}/activity/user/${currentUser}/${category}/${subcategory}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ value, weight, learnedFrom })
      });

      if (response.ok) {
        const result = await response.json();
        console.log(`✅ Set ${category}.${subcategory} activity preference in database`);
        return result;
      } else {
        console.warn('⚠️ Database activity preference set failed, falling back to localStorage');
        return this.setActivityPreferenceInLocalStorage(category, subcategory, value, weight);
      }
    } catch (error) {
      console.error('Error setting activity preference in database:', error);
      return this.setActivityPreferenceInLocalStorage(category, subcategory, value, weight);
    }
  }

  // === UTILITY METHODS ===

  // Generate device ID
  static getDeviceId() {
    let deviceId = localStorage.getItem('deviceId');
    if (!deviceId) {
      deviceId = generateId('device');
      localStorage.setItem('deviceId', deviceId);
    }
    return deviceId;
  }

  // Detect device type
  static getDeviceType() {
    const userAgent = navigator.userAgent;
    if (/tablet|ipad|playbook|silk/i.test(userAgent)) {
      return 'tablet';
    }
    if (/mobile|iphone|ipod|android|blackberry|opera|mini|windows\sce|palm|smartphone|iemobile/i.test(userAgent)) {
      return 'mobile';
    }
    return 'desktop';
  }

  // === LOCALSTORAGE FALLBACK METHODS ===

  static getPreferencesFromLocalStorage() {
    try {
      const preferences = localStorage.getItem('userPreferences');
      return preferences ? JSON.parse(preferences) : {};
    } catch (error) {
      console.error('Error reading preferences from localStorage:', error);
      return {};
    }
  }

  static getCategoryFromLocalStorage(category) {
    const allPrefs = this.getPreferencesFromLocalStorage();
    return allPrefs[category] || {};
  }

  static setPreferenceInLocalStorage(category, key, value) {
    try {
      const preferences = this.getPreferencesFromLocalStorage();
      if (!preferences[category]) {
        preferences[category] = {};
      }
      preferences[category][key] = value;
      localStorage.setItem('userPreferences', JSON.stringify(preferences));
      return { success: true };
    } catch (error) {
      console.error('Error setting preference in localStorage:', error);
      return { success: false, error: error.message };
    }
  }

  static setMultiplePreferencesInLocalStorage(preferences) {
    try {
      const existingPreferences = this.getPreferencesFromLocalStorage();
      const merged = { ...existingPreferences, ...preferences };
      localStorage.setItem('userPreferences', JSON.stringify(merged));
      return { success: true, updatedCount: Object.keys(preferences).length };
    } catch (error) {
      console.error('Error setting multiple preferences in localStorage:', error);
      return { success: false, error: error.message };
    }
  }

  static deletePreferenceFromLocalStorage(category, key) {
    try {
      const preferences = this.getPreferencesFromLocalStorage();
      if (preferences[category]) {
        delete preferences[category][key];
        localStorage.setItem('userPreferences', JSON.stringify(preferences));
      }
      return { success: true };
    } catch (error) {
      console.error('Error deleting preference from localStorage:', error);
      return { success: false, error: error.message };
    }
  }

  static getDevicePreferencesFromLocalStorage() {
    try {
      const devicePrefs = localStorage.getItem('devicePreferences');
      return devicePrefs ? JSON.parse(devicePrefs) : {};
    } catch (error) {
      console.error('Error reading device preferences from localStorage:', error);
      return {};
    }
  }

  static setDevicePreferencesInLocalStorage(preferences) {
    try {
      localStorage.setItem('devicePreferences', JSON.stringify(preferences));
      return { success: true };
    } catch (error) {
      console.error('Error setting device preferences in localStorage:', error);
      return { success: false, error: error.message };
    }
  }

  static getActivityPreferencesFromLocalStorage(category) {
    try {
      const activityPrefs = localStorage.getItem(`activityPreferences_${category}`);
      return activityPrefs ? JSON.parse(activityPrefs) : [];
    } catch (error) {
      console.error(`Error reading ${category} activity preferences from localStorage:`, error);
      return [];
    }
  }

  static setActivityPreferenceInLocalStorage(category, subcategory, value, weight) {
    try {
      const preferences = this.getActivityPreferencesFromLocalStorage(category);
      const existingIndex = preferences.findIndex(p => p.subcategory === subcategory);
      
      const preference = {
        subcategory,
        value,
        weight: weight || 1.0,
        learnedFrom: 'explicit',
        updatedAt: new Date().toISOString()
      };

      if (existingIndex >= 0) {
        preferences[existingIndex] = preference;
      } else {
        preferences.push(preference);
      }

      localStorage.setItem(`activityPreferences_${category}`, JSON.stringify(preferences));
      return { success: true };
    } catch (error) {
      console.error(`Error setting ${category} activity preference in localStorage:`, error);
      return { success: false, error: error.message };
    }
  }

  // === CONVENIENCE METHODS FOR COMMON PREFERENCES ===

  // Notification preferences
  static async getNotificationPreferencesAsync(userId = null) {
    return await this.getPreferencesByCategoryAsync('notification', userId);
  }

  static async setNotificationPreferencesAsync(preferences, userId = null) {
    return await this.setPreferenceAsync('notification', 'all', preferences, userId);
  }

  // Search preferences
  static async getSearchPreferencesAsync(userId = null) {
    return await this.getPreferencesByCategoryAsync('search', userId);
  }

  static async setSearchPreferencesAsync(preferences, userId = null) {
    return await this.setPreferenceAsync('search', 'all', preferences, userId);
  }

  // UI preferences
  static async getUIPreferencesAsync(userId = null) {
    return await this.getPreferencesByCategoryAsync('ui', userId);
  }

  static async setUIPreferencesAsync(preferences, userId = null) {
    return await this.setPreferenceAsync('ui', 'all', preferences, userId);
  }

  // Privacy preferences
  static async getPrivacyPreferencesAsync(userId = null) {
    return await this.getPreferencesByCategoryAsync('privacy', userId);
  }

  static async setPrivacyPreferencesAsync(preferences, userId = null) {
    return await this.setPreferenceAsync('privacy', 'all', preferences, userId);
  }
}

export default UserPreferencesService;